import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  Bell,
  FolderOpen,
  LayoutDashboard,
  Plug,
  Server,
  Package,
  ListTodo,
  Settings,
  Activity,
  LogOut,
  TrendingUp,
} from 'lucide-react'
import { useHealthCheck, useAlertStats } from '@/hooks/useApi'
import { useAuth } from '@/context/AuthContext'
import { NotificationBell } from '@/components/layout/NotificationBell'

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Dossiers', href: '/dossiers', icon: FolderOpen },
  { name: 'SEO-verbeterplan', href: '/seo', icon: TrendingUp },
  { name: 'Scans', href: '/scans', icon: ListTodo },
  { name: 'Alerts', href: '/alerts', icon: Bell },
  { name: 'Inventaris', href: '/inventory', icon: Package },
  { name: 'Integraties', href: '/integrations', icon: Plug },
  { name: 'Instellingen', href: '/settings', icon: Settings },
  { name: 'Systeem', href: '/system', icon: Server },
]

interface LayoutProps {
  children: React.ReactNode
}

export function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { data: health } = useHealthCheck()
  const { data: alertStats } = useAlertStats()
  const { user, logout } = useAuth()

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 border-r bg-card">
        <div className="flex h-16 items-center justify-between border-b px-4">
          <div className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            <span className="text-lg font-semibold">Techvibe</span>
          </div>
          <NotificationBell />
        </div>

        <nav className="flex flex-col gap-1 p-4">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href
            // Only critical, high and KEV get a badge. Counting every open
            // alert put a red "9+" next to a list that was half informational
            // maintenance rows, which trains you to ignore it.
            const badge = item.href === '/alerts' ? alertStats?.actionable ?? 0 : 0
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{item.name}</span>
                {badge > 0 && (
                  <span
                    className="rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white"
                    title={`${badge} alert(s) met ernst hoog, kritiek of KEV`}
                  >
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </Link>
            )
          })}
        </nav>

        {/* Bottom section: user + health */}
        <div className="absolute bottom-0 left-0 right-0 border-t">
          {/* User + Logout */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="truncate text-sm text-muted-foreground">
              {user?.username}
            </span>
            <button
              onClick={logout}
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              title="Uitloggen"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>

          {/* Health Status */}
          <div className="border-t px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <div
                className={cn(
                  'h-2 w-2 rounded-full',
                  health?.status === 'healthy'
                    ? 'bg-green-500'
                    : health?.status === 'degraded'
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                )}
              />
              <span className="text-muted-foreground">
                Systeem {health?.status === 'healthy' ? 'gezond' : health?.status === 'degraded' ? 'verstoord' : 'controleert...'}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="pl-64">
        <div className="container mx-auto p-8">{children}</div>
      </main>
    </div>
  )
}
